import { useState, useEffect } from 'react';
import { 
  getPptxFile,
  updatePptxFile,
  deletePptxFile,
  publishPptxFile,
  updateSlideDescription,
  generateDescriptionFromSlide,
  uploadThumbnail,
  deleteThumbnail,
  generatePptxDescription,
  updateSlideElementLocked
} from '../services/pptxFiles';
import { ParsedSlide, PptxFileUpdate, PptxFile } from '../types';

export function usePptxFile(fileId: string | undefined) {
  const [pptxFile, setPptxFile] = useState<PptxFile | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (fileId) {
      fetchPptxFile();
    } else {
      setPptxFile(null);
      setError(null);
      setLoading(false);
    }
  }, [fileId]);

  const fetchPptxFile = async () => {
    if (!fileId) {
      setPptxFile(null);
      return;
    }

    setLoading(true);
    try {
      const file = await getPptxFile(fileId);
      setPptxFile(file);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch PPTX file'));
      setPptxFile(null);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePptxFile = async (updates: Partial<PptxFileUpdate>) => {
    if (!fileId || !pptxFile) return;

    try {
      const updatedFile = await updatePptxFile(fileId, updates);
      setPptxFile(updatedFile);
      return updatedFile;
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to update PPTX file'));
      throw err;
    }
  };

  const handleDeletePptxFile = async () => {
    if (!fileId) return;

    try {
      await deletePptxFile(fileId);
      setPptxFile(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to delete PPTX file'));
      throw err;
    }
  };

  const handlePublishPptxFile = async () => {
    if (!fileId) return;

    try {
      const publishedFile = await publishPptxFile(fileId);
      setPptxFile(publishedFile);
      return publishedFile;
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to publish PPTX file'));
      throw err;
    }
  };

  const handleUpdateSlideDescription = async (slideNumber: number, description: string) => {
    if (!fileId || !pptxFile) return;

    try {
      setPptxFile(prevFile => {
        if (!prevFile) return null;
        return {
          ...prevFile,
          parsedSlides: prevFile.parsedSlides.map((slide: ParsedSlide, index: number) =>
            index + 1 === slideNumber ? { ...slide, description } : slide
          ),
        };
      });
      const updatedSlide = await updateSlideDescription(fileId, slideNumber, description);
      return updatedSlide;
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to update slide description'));
      throw err;
    }
  };

  const handleGenerateDescriptionFromSlide = async (slideNumber: number, { lang }: { lang: string }) => {
    if (!fileId) return;

    try {
      const { description } = await generateDescriptionFromSlide(fileId, slideNumber, { lang });
      return description;
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to generate slide description'));
      throw err;
    }
  };

  const handleUploadThumbnail = async (slideNumber: number, file: File) => {
    if (!fileId) return;

    try {
      await uploadThumbnail(fileId, slideNumber, file);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to upload thumbnail'));
      throw err;
    }
  };

  const handleDeleteThumbnail = async (slideNumber: number) => {
    if (!fileId) return;

    try {
      await deleteThumbnail(fileId, slideNumber);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to delete thumbnail'));
      throw err;
    }
  };

  const handleGeneratePptxDescription = async ({ lang }: { lang: string }) => {
    if (!fileId) return;

    try {
      const { description } = await generatePptxDescription(fileId, { lang });
      return description;
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to generate PPTX description'));
      throw err;
    }
  };

  const handleUpdateSlideElementLocked = async (slideNumber: number, elementIndex: number, isLocked: boolean) => {
    if (!fileId || !pptxFile) return;

    try {
      const updatedSlide = await updateSlideElementLocked(fileId, slideNumber, elementIndex, isLocked);
      
      // Update local state
      setPptxFile(prevFile => {
        if (!prevFile) return null;
        return {
          ...prevFile,
          parsedSlides: prevFile.parsedSlides.map((slide: ParsedSlide, index: number) =>
            index + 1 === slideNumber 
              ? updatedSlide 
              : slide
          ),
        };
      });
      
      return updatedSlide;
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to update element lock status'));
      throw err;
    }
  };

  return {
    pptxFile,
    loading,
    error,
    fetchPptxFile,
    handleUpdatePptxFile,
    handleDeletePptxFile,
    handlePublishPptxFile,
    handleUpdateSlideDescription,
    handleGenerateDescriptionFromSlide,
    handleUploadThumbnail,
    handleDeleteThumbnail,
    handleGeneratePptxDescription,
    handleUpdateSlideElementLocked,
  };
}
